import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuthSession } from "@/hooks/useAuthSession";
import { ProtectedErrorBoundary } from "./ProtectedErrorBoundary";

type Props = { children: ReactNode };

/**
 * Gates every /seo-* route. Stays neutral while loading; redirects only
 * after auth state has fully hydrated. No fixed standalone Logout button —
 * logout lives only in the shared header.
 */
export const AdminGuard = ({ children }: Props) => {
  const { loading, session, isAdmin } = useAuthSession();
  const location = useLocation();

  if (loading) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex,nofollow,noarchive" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-sm text-slate-500">Loading…</div>
        </div>
      </>
    );
  }

  if (!session) {
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Helmet>
          <meta name="robots" content="noindex,nofollow,noarchive" />
        </Helmet>
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Not authorised</h2>
          <p className="text-sm text-slate-600">
            This account is signed in but does not have dashboard access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex,nofollow,noarchive" />
      </Helmet>
      <ProtectedErrorBoundary>{children}</ProtectedErrorBoundary>
    </>
  );
};

export default AdminGuard;
